import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames/bind';
import * as R from 'ramda';

import { DataBlocks, Link, Tooltip, Icon, I18n, ErrorBlock } from 'components';
import printAccumulator, { getProgressBarColor } from 'utils/accumulators';
import {
  formatUnitString,
  formatDateString,
} from 'utils/formatString';
import { openPopup } from 'actions';
import * as dvr from 'utils/dvr';
import TariffConstructor from '../TariffConstructor';
import { refetchData } from '../../actions';

import styles from './styles.pcss';
import { ymPushParams } from '../../../../utils/analytics/ym';

const cx = classNames.bind(styles);

const Accumulators = ({
  data = {},
  data: {
    list,
    constructor,
    changeTexts,
    progressBarLimits,
  },
  error,
  actions,
  isBlocked,
}) => {
  const getNewAccumulators = (accumulatorsData) => {
    const fatherFamilyAccumulatorData = accumulatorsData.find(({ presentType, isUnlimited, showAccumulators }) => (
      presentType === 'UNL_PRES' && isUnlimited && showAccumulators
    ));

    const fatherIndex = accumulatorsData.indexOf(fatherFamilyAccumulatorData) + 1;

    if (fatherFamilyAccumulatorData) {
      const fatherFamilyAccumulator = {};

      for (const key in fatherFamilyAccumulatorData) {
        if (key !== 'presentType' && key !== 'showAccumulators' && key !== 'unlimExpirationDate') {
          fatherFamilyAccumulator[key] = fatherFamilyAccumulatorData[key];
        }
      }

      fatherFamilyAccumulator.isUnlimited = false;
      accumulatorsData.splice(fatherIndex, 0, fatherFamilyAccumulator);
    }

    return accumulatorsData;
  };

  if (error?.type || data?.error) {
    return (
      <ErrorBlock
        title={<I18n path="home.accumulators.blockTitle" />}
        refreshAction={() => actions.refetchData('Accumulators')}
      />
    );
  }

  if (!data || !list?.length) return null;

  const accumulators = getNewAccumulators(list);

  const hasPresent = accumulators.find((item) => {
    const { presentType, unlimExpirationDate, isUnlimited } = item;

    return presentType === 'UNL_PRES' && unlimExpirationDate && isUnlimited;
  });

  const onClickGoTraffic = () => {
    // отлючил линтер для camelCase тк это для яндекс метрики
    // eslint-disable-next-line camelcase
    ymPushParams({ personal_account: { extra_internet: { lk_old: { block_mobile: {
      button_name: 'Пополнить интернет', // eslint-disable-line camelcase
    } } } } });
  };

  return (
    <DataBlocks.Block
      title={<I18n path="home.accumulators.blockTitle" />}
      info={<I18n path="home.accumulators.blockDescription" />}
      className={cx({ hasPresent })}
    >
      {typeof constructor === 'object' && (
        <TariffConstructor
          isDisabled={isBlocked}
          description={changeTexts}
          {...constructor}
        />
      )}

      <DataBlocks.Table>
        {accumulators.map((acc, index) => {
          const {
            title,
            presentType,
            icon,
            description,
            speedUp,
            isInternetTrafficLow,
            hasTopUpBalance,
            isUnlimited,
            size,
            rest,
            unit,
            packages,
            options,
            resetDate,
            nextValue,
            unlimExpirationDate,
          } = acc;

          return (
            <DataBlocks.TableRow
              key={`accumulator-unit-${title}-${index}`}
              className={cx(hasPresent && presentType === 'UNL_PRES' && 'present')}
            >
              <DataBlocks.TableCell
                className={cx('tableCell', 'titleCell')}
              >
                <div className={cx('accumulatorTitle')}>
                  <span className={cx('iconCircle')}>
                    <img className={cx('iconImage')} src={icon} alt="" />
                  </span>
                  <span className={cx('accumulatorName')}>
                    {title}
                    { (presentType !== 'UNL_PRES') && (
                    <Tooltip>
                      <span dangerouslySetInnerHTML={{ __html: description }} />
                    </Tooltip>
                    )}
                  </span>
                  {speedUp && (
                  <div className={cx('accumulatorSpeedUp')}>
                    <Icon
                      className={cx('accumulatorSpeedUpIcon')}
                      name="check-circle"
                    />
                    <Link
                      className={cx('accumulatorSpeedUpTitle')}
                      href={speedUp?.url}
                    >
                      {speedUp?.type === 'Auto' || !speedUp?.title ? (
                        <I18n path="home.accumulators.autoSpeedUp" />
                      ) : speedUp?.title}
                    </Link>
                    {speedUp?.enabled && (
                      <Tooltip width="200">
                        <I18n path="home.accumulators.autoSpeedUpTooltip" />
                      </Tooltip>
                    )}
                  </div>
                  )}
                  {isInternetTrafficLow && !isUnlimited && (
                  <div className={cx('accumulatorSpeedUpLink')}>
                    <Link
                      onClick={() => {
                        actions.openPopup('accumulatorsSpeedUp');
                        dvr.playWaw('IVR_int.wav');
                      }}
                    >
                      <I18n path="home.accumulators.speedUpLink" />
                    </Link>
                  </div>
                  )}
                </div>
              </DataBlocks.TableCell>
              <DataBlocks.TableCell className={cx('tableCell')}>
                {isUnlimited ? (
                  <I18n
                    path="home.accumulators.unlimited"
                    className={cx('unlimitedText')}
                  />
                ) : (
                  <>
                    <div className={cx('accumulatorValue')}>
                      <I18n
                        path="home.accumulators.accumulatorInfo"
                        vars={{ info: printAccumulator(acc) }}
                      />
                    </div>
                    {size >= rest && (
                    <div className={cx('accumulatorProgress')}>
                      <div className={cx('progressBar')}>
                        <div
                          className={cx('progressBarValue')}
                          style={{ width: `${rest / size * 100}%` }}
                        >
                          <div
                            className={cx(
                              'progressBarFill',
                              getProgressBarColor(acc, progressBarLimits),
                            )}
                          />
                        </div>
                      </div>

                      {(packages || options) && (
                        <Tooltip width={240}>
                          {packages?.length > 0 && packages.map((pack) => (
                            <div className={cx('package')} key={pack.title}>
                              <div className={cx('packageSize')}>
                                {formatUnitString(pack.size, unit)}
                              </div>
                              <div
                                className={cx('packageName')}
                                dangerouslySetInnerHTML={{
                                  __html: pack.title,
                                }}
                              />
                            </div>
                          ))}
                          {options && options.map((pkg) => (
                            <div
                              key={pkg.groupPackageName}
                              className={cx('package')}
                            >
                              <div className={cx('packageSize')}>
                                {pkg.countPackageByGroup > 1
                                  ? `${pkg.countPackageByGroup} x `
                                  : ''}
                                {formatUnitString(pkg.groupPackageVolume, unit)}
                              </div>
                              <div
                                className={cx('packageName')}
                                dangerouslySetInnerHTML={{ __html: pkg.groupPackageName }}
                              />
                            </div>
                          ))}
                        </Tooltip>
                      )}
                    </div>
                    )}
                    { hasTopUpBalance && (
                      <Link
                        href="https://beeline.ru/traffic/"
                        onClick={onClickGoTraffic}
                      >
                        Пополнить интернет
                      </Link>
                    )}
                  </>
                )}
              </DataBlocks.TableCell>
              <DataBlocks.TableCell className={cx('tableCell', 'infoCell')}>
                {resetDate && !isUnlimited && (
                <div className={cx('updateInfo')}>
                  {unit === 'KBYTE' ? (
                    <I18n
                      path="home.accumulators.updateDate"
                      vars={{
                        date: formatDateString(resetDate, 'DD.MM.YY'),
                      }}
                    />
                  ) : (
                    <I18n
                      path="home.accumulators.updateDateSize"
                      vars={{
                        date: formatDateString(resetDate, 'DD.MM.YY'),
                        size: formatUnitString(
                          nextValue || size,
                          unit,
                        ),
                      }}
                    />
                  )}
                </div>
                )}
                {hasPresent && presentType === 'UNL_PRES' && (
                <I18n
                  path="home.accumulators.unlimGiftedExpDate"
                  vars={{
                    date: formatDateString(unlimExpirationDate, 'D MMMM'),
                  }}
                  className={cx('unlimitedDate')}
                />
                )}
              </DataBlocks.TableCell>
            </DataBlocks.TableRow>
          );
        })}
      </DataBlocks.Table>
    </DataBlocks.Block>
  );
};

Accumulators.propTypes = {
  data: PropTypes.shape({
    changeTexts: PropTypes.object,
    constructor: PropTypes.object,
    progressBarLimits: PropTypes.object,
    list: PropTypes.array,
  }),
  error: PropTypes.object,
  actions: PropTypes.object,
  isBlocked: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const status = R.path(['home', 'status', 'data', 'status'], state);

  return {
    data: state.home.accumulators.data,
    error: state.home.accumulators.error,
    isBlocked: status && status !== 'Active',
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    openPopup,
    refetchData,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Accumulators);
